import React from "react";
import { ShoppingCart, WhatsApp } from "@mui/icons-material";
import { BranchProduct } from "../../../types";

interface ProductActionButtonsProps {
  product: BranchProduct;
  quantity: number;
  getCardColors: string;
  t: (key: string, options?: any) => string;
  onAddToCart: (qty: number) => void;
  onWhatsAppPurchase: (qty: number) => void;
  disabled?: boolean;
  variant?: "default" | "compact" | "full" | "vertical";
}

const ProductActionButtons: React.FC<ProductActionButtonsProps> = ({
  product,
  quantity,
  getCardColors,
  t,
  onAddToCart,
  onWhatsAppPurchase,
  disabled = false,
  variant = "default",
}) => {
  // الحصول على النصوص المترجمة
  const addToCartText = t("productCard.addToCart", "Add to Cart");
  const outOfStockText = t("productCard.outOfStock", "Out of Stock");
  const whatsappPurchaseText = t("productCard.whatsappPurchase", "Buy on WhatsApp");
  const notifyMeText = t("productCard.notifyMe", "Notify Me");
  const fastDeliveryText = t("productCard.fastDelivery", "Fast Delivery");
  const currentlyUnavailableText = t("productCard.currentlyUnavailable", "Currently Unavailable");
  const cartText = t("productCard.cart", "Cart");
  const whatsappText = t("productCard.whatsapp", "WhatsApp");

  // تصميم عمودي للأزرار (تحت بعض) - مناسب للعرض الرأسي
  if (variant === "vertical") {
    return (
      <div className="space-y-2 w-full">
        {/* زر إضافة إلى السلة */}
        <button
          className={`w-full py-3 bg-gradient-to-r ${getCardColors} text-white font-bold rounded-lg hover:opacity-90 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md`}
          onClick={() => onAddToCart(quantity)}
          disabled={!product.is_available || disabled}
          aria-label={product.is_available ? addToCartText : outOfStockText}
        >
          <ShoppingCart className="w-4 h-4 md:w-5 md:h-5" />
          <span className="text-sm md:text-base">
            {product.is_available ? addToCartText : outOfStockText}
          </span>
          {quantity > 1 && (
            <span className="text-xs bg-white/30 px-2 py-0.5 rounded-full">
              {quantity}
            </span>
          )}
        </button>

        {/* زر الشراء عبر واتساب */}
        {product.is_available && (
          <button
            className="w-full py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all duration-300 flex items-center justify-center gap-2 shadow-sm hover:shadow-md"
            onClick={() => onWhatsAppPurchase(quantity)}
            disabled={disabled}
            aria-label={whatsappPurchaseText}
          >
            <WhatsApp className="w-4 h-4 md:w-5 md:h-5" />
            <span className="text-sm md:text-base">
              {whatsappPurchaseText}
            </span>
          </button>
        )}

        {/* بديل إذا كان المنتج غير متوفر */}
        {!product.is_available && (
          <button
            className="w-full py-3 bg-gradient-to-r from-gray-400 to-gray-500 text-white font-bold rounded-lg cursor-not-allowed flex items-center justify-center gap-2 shadow-sm"
            disabled
            aria-label={notifyMeText}
          >
            <svg
              className="w-4 h-4 md:w-5 md:h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="text-sm md:text-base">
              {notifyMeText}
            </span>
          </button>
        )}
      </div>
    );
  }

  // تصميم مضغوط للأجهزة الصغيرة
  if (variant === "compact") {
    return (
      <div className="flex gap-2 flex-wrap">
        <button
          className={`flex-1 py-2 text-xs bg-gradient-to-r ${getCardColors} text-white font-bold rounded-lg hover:opacity-90 transition-all duration-300 flex items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed`}
          onClick={() => onAddToCart(quantity)}
          disabled={!product.is_available || disabled}
          title={product.is_available ? addToCartText : outOfStockText}
          aria-label={product.is_available ? addToCartText : outOfStockText}
        >
          <ShoppingCart className="w-3 h-3" />
          <span className="truncate">
            {product.is_available ? cartText : outOfStockText}
          </span>
        </button>
        
        {product.is_available && (
          <button
            className="flex-1 py-2 text-xs bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all duration-300 flex items-center justify-center gap-1"
            onClick={() => onWhatsAppPurchase(quantity)}
            disabled={disabled}
            title={whatsappPurchaseText}
            aria-label={whatsappPurchaseText}
          >
            <WhatsApp className="w-3 h-3" />
            <span className="truncate">
              {whatsappText}
            </span>
          </button>
        )}
      </div>
    );
  }

  // تصميم كامل (عمودي محسّن) - الافتراضي
  return (
    <div className="space-y-3 w-full">
      {/* زر إضافة إلى السلة */}
      <button
        className={`w-full py-3 px-3 bg-gradient-to-r ${getCardColors} text-white font-bold rounded-xl hover:shadow-lg transition-all duration-300 flex items-center justify-between group relative overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed`}
        onClick={() => onAddToCart(quantity)}
        disabled={!product.is_available || disabled}
        aria-label={product.is_available ? addToCartText : outOfStockText}
      >
        {/* تأثير الخلفية المتحركة */}
        <div 
          className="absolute inset-0 bg-white/10 transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300"
          aria-hidden="true"
        />
        
        {/* المحتوى */}
        <div className="flex items-center gap-2 z-10">
          <div className="bg-white/20 p-1.5 rounded-lg">
            <ShoppingCart className="w-4 h-4 md:w-5 md:h-5" aria-hidden="true" />
          </div>
          <div className="text-right">
            <div className="text-sm md:text-base font-bold">
              {product.is_available ? addToCartText : outOfStockText}
            </div>
            {quantity > 1 && product.is_available && (
              <div className="text-xs opacity-90">
                {quantity} × {product.price} {t("common.currency", "EGP")}
              </div>
            )}
          </div>
        </div>
        
        {/* السعر الإجمالي */}
        {product.is_available && (
          <div className="z-10 text-sm md:text-base font-bold bg-white/20 px-2 py-1 rounded-lg">
            {(product.price * quantity).toFixed(2)} {t("common.currency", "EGP")}
          </div>
        )}
      </button>

      {/* زر الشراء عبر واتساب */}
      {product.is_available && (
        <button
          className="w-full py-3 px-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all duration-300 flex items-center justify-between group relative overflow-hidden"
          onClick={() => onWhatsAppPurchase(quantity)}
          disabled={disabled}
          aria-label={whatsappPurchaseText}
        >
          {/* تأثير الخلفية المتحركة */}
          <div 
            className="absolute inset-0 bg-white/10 transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300"
            aria-hidden="true"
          />
          
          {/* المحتوى */}
          <div className="flex items-center gap-2 z-10">
            <div className="bg-white/20 p-1.5 rounded-lg">
              <WhatsApp className="w-4 h-4 md:w-5 md:h-5" aria-hidden="true" />
            </div>
            <div className="text-right">
              <div className="text-sm md:text-base font-bold">
                {whatsappPurchaseText}
              </div>
              <div className="text-xs opacity-90">
                {fastDeliveryText}
              </div>
            </div>
          </div>
          
          {/* أيقونة السهم */}
          <svg
            className="w-4 h-4 md:w-5 md:h-5 z-10 transform group-hover:translate-x-1 transition-transform"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M14 5l7 7m0 0l-7 7m7-7H3"
            />
          </svg>
        </button>
      )}

      {/* رسالة إذا كان المنتج غير متوفر */}
      {!product.is_available && (
        <div 
          className="bg-amber-50 border border-amber-200 rounded-xl p-2 flex items-center justify-between"
          role="status"
          aria-live="polite"
        >
          <div className="flex items-center gap-2 text-amber-800">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.346 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
            <span className="text-sm font-medium">
              {currentlyUnavailableText}
            </span>
          </div>
          <button 
            className="text-xs bg-amber-500 hover:bg-amber-600 text-white px-2 py-1 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-amber-300"
            onClick={() => {/* إضافة وظيفة الإشعار هنا */}}
            aria-label={notifyMeText}
          >
            {notifyMeText}
          </button>
        </div>
      )}
    </div>
  );
};



export default ProductActionButtons;